/** @format */

import React, { memo } from "react";
import type { Column, CellSelection } from "./types";

interface VirtualRowProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowData: any;
  rowIndex: number;
  rowTop: number;
  rowHeight: number;
  totalWidth: number;
  columns: Column[];
  columnWidths?: Record<string, number>;
  borderColor: string;
  backgroundColor: string;
  hoverBackgroundColor: string;
  rowColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowClick?: (record: any, index: number) => void;
  customClassName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCell: (column: Column, record: any, index: number) => React.ReactNode;
  enableCellSelection?: boolean;
  selectedCells?: CellSelection[];
  selectedCellsSet?: Set<string>; // Performance optimization
  activeCell?: { rowIndex: number; columnKey: string } | null;
  onCellClick?: (
    rowIndex: number,
    columnKey: string,
    value: unknown,
    event: React.MouseEvent
  ) => void;
  onCellMouseDown?: (
    rowIndex: number,
    columnKey: string,
    event: React.MouseEvent
  ) => void;
  onCellMouseMove?: (rowIndex: number, columnKey: string) => void;
  onCellMouseUp?: () => void;
  enableColumnReorder?: boolean;
  draggedColumnIndex?: number | null;
  dragOverColumnIndex?: number | null;
  // Row selection
  enableRowSelection?: boolean;
  isRowSelected?: boolean;
  onToggleRowSelection?: () => void;
  selectionColWidth?: number;
  checkboxSize?: number;
}

const VirtualRow: React.FC<VirtualRowProps> = memo(
  ({
    rowData,
    rowIndex,
    rowTop,
    rowHeight,
    totalWidth,
    columns,
    columnWidths = {},
    borderColor,
    backgroundColor,
    hoverBackgroundColor,
    rowColor,
    onRowClick,
    customClassName,
    renderCell,
    enableCellSelection = false,
    selectedCells = [],
    selectedCellsSet,
    activeCell,
    onCellClick,
    onCellMouseDown,
    onCellMouseMove,
    onCellMouseUp,
    enableColumnReorder = false,
    draggedColumnIndex = null,
    dragOverColumnIndex = null,
    enableRowSelection = false,
    isRowSelected = false,
    onToggleRowSelection,
    selectionColWidth = 48,
    checkboxSize = 20,
  }) => {
    return (
      <div
        className={`virtual-grid-row ${customClassName || ""} ${
          isRowSelected ? "row-selected" : ""
        }`}
        style={{
          position: "absolute",
          top: `${rowTop}px`,
          left: 0,
          width: `${totalWidth}px`,
          height: `${rowHeight}px`,
          display: "flex",
          background: backgroundColor,
          borderBottom: `1px solid ${borderColor}`,
          cursor: onRowClick ? "pointer" : "default",
          // @ts-expect-error - CSS custom property
          "--hover-bg": hoverBackgroundColor,
        }}
        onClick={() => onRowClick?.(rowData, rowIndex)}
      >
        {enableRowSelection && (
          <div
            className="virtual-grid-cell"
            style={{
              width: `${selectionColWidth}px`,
              minWidth: `${selectionColWidth}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRight: `1px solid ${borderColor}`,
              padding: 0,
            }}
          >
            <button
              type="button"
              aria-label="Select row"
              onClick={(e) => {
                e.stopPropagation();
                onToggleRowSelection?.();
              }}
              className={`vg-checkbox ${isRowSelected ? "checked" : ""}`}
              style={{
                width: `${checkboxSize}px`,
                height: `${checkboxSize}px`,
              }}
            >
              {isRowSelected && (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M20 6L9 17l-5-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
        {columns.map((column, colIndex) => {
          // Performance optimization: Use Set for O(1) lookup instead of O(n) array.some
          const isSelected =
            enableCellSelection &&
            (selectedCellsSet
              ? selectedCellsSet.has(`${rowIndex}-${column.key}`)
              : selectedCells.some(
                  (cell) =>
                    cell.rowIndex === rowIndex && cell.columnKey === column.key
                ));

          const isActive =
            activeCell?.rowIndex === rowIndex &&
            activeCell?.columnKey === column.key;

          const columnWidth = columnWidths[column.key] || column.width || 150;

          // Check if this column is being dragged or is drag target
          const isDraggingThisColumn =
            enableColumnReorder && draggedColumnIndex === colIndex;
          const isDragOverThisColumn =
            enableColumnReorder && dragOverColumnIndex === colIndex;

          // Determine background color
          let cellBackgroundColor = "transparent";
          if (isSelected && !isActive) {
            // Other selected cells: light blue with opacity
            cellBackgroundColor = "rgba(24, 144, 255, 0.15)";
          } else if (isActive) {
            // Active cell: transparent with blue border only
            cellBackgroundColor = "transparent";
          } else if (isDragOverThisColumn) {
            // Column being dragged over: light blue background
            cellBackgroundColor = "rgba(24, 144, 255, 0.1)";
          }

          return (
            <div
              key={column.key}
              className={`virtual-grid-cell ${
                isSelected ? "cell-selected" : ""
              } ${isActive ? "cell-active" : ""}`}
              style={{
                width: `${columnWidth}px`,
                minWidth: `${column.minWidth || columnWidth}px`,
                textAlign: column.align || "left",
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                borderRight: `1px solid ${borderColor}`,
                color: rowColor,
                backgroundColor: cellBackgroundColor,
                cursor: enableCellSelection ? "pointer" : "default",
                opacity: isDraggingThisColumn ? 0.5 : 1,
                userSelect: enableCellSelection ? "none" : "auto",
                outline: isActive ? "2px solid #1890ff" : "none",
                outlineOffset: "-2px",
              }}
              onClick={(e) => {
                if (enableCellSelection && onCellClick) {
                  const value = rowData[column.dataIndex];
                  onCellClick(rowIndex, column.key, value, e);
                }
              }}
              onMouseDown={(e) => {
                if (enableCellSelection && onCellMouseDown) {
                  onCellMouseDown(rowIndex, column.key, e);
                }
              }}
              onMouseMove={() => {
                if (enableCellSelection && onCellMouseMove) {
                  onCellMouseMove(rowIndex, column.key);
                }
              }}
              onMouseUp={() => {
                if (enableCellSelection && onCellMouseUp) {
                  onCellMouseUp();
                }
              }}
            >
              {renderCell(column, rowData, rowIndex)}
            </div>
          );
        })}
      </div>
    );
  },
  // Custom comparison function for better performance
  (prevProps, nextProps) => {
    // Check if columnWidths changed
    const columnWidthsChanged =
      JSON.stringify(prevProps.columnWidths) !==
      JSON.stringify(nextProps.columnWidths);

    return (
      prevProps.rowIndex === nextProps.rowIndex &&
      prevProps.rowTop === nextProps.rowTop &&
      prevProps.rowData === nextProps.rowData &&
      prevProps.backgroundColor === nextProps.backgroundColor &&
      prevProps.hoverBackgroundColor === nextProps.hoverBackgroundColor &&
      prevProps.rowColor === nextProps.rowColor &&
      prevProps.customClassName === nextProps.customClassName &&
      prevProps.enableCellSelection === nextProps.enableCellSelection &&
      prevProps.selectedCells?.length === nextProps.selectedCells?.length &&
      prevProps.activeCell?.rowIndex === nextProps.activeCell?.rowIndex &&
      prevProps.activeCell?.columnKey === nextProps.activeCell?.columnKey &&
      prevProps.enableColumnReorder === nextProps.enableColumnReorder &&
      prevProps.draggedColumnIndex === nextProps.draggedColumnIndex &&
      prevProps.dragOverColumnIndex === nextProps.dragOverColumnIndex &&
      prevProps.enableRowSelection === nextProps.enableRowSelection &&
      prevProps.isRowSelected === nextProps.isRowSelected &&
      !columnWidthsChanged &&
      (prevProps.selectedCells?.every(
        (cell, index) =>
          cell.rowIndex === nextProps.selectedCells?.[index]?.rowIndex &&
          cell.columnKey === nextProps.selectedCells?.[index]?.columnKey
      ) ??
        true)
    );
  }
);

VirtualRow.displayName = "VirtualRow";

export default VirtualRow;
